<template>
    <div v-if="event" class="space-y-4">
        <FlexibleCard :headline-id="identifiersCardId" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus(identifiersCardId)"
                    :edit-button-disabled-explanation="editButtonDisabledMessage(identifiersCardId)"
                    :save-button-disabled-explanation="saveButtonDisabledMessage(identifiersCardId)"
                    @enter-edit-mode="onEnterEditMode(identifiersCardId, identifiersCardLabel)"
                    @leave-edit-mode="onLeaveEditMode"
                    @discard-changes="onDiscardChanges"
                    @save="onSave"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ identifiersCardLabel }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>
            <template #body>
                <ContentFormTransition>
                    <BaseForm v-if="showForm(identifiersCardId)" class="p-5" @submit.prevent>
                        <template #fields>
                            <TextField
                                :value="values.nameShort"
                                :label="$t('name-short')"
                                :error-messages="errors.nameShort"
                                :required="true"
                                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('nameShort', true)"
                            />
                            <TextField
                                :value="values.nameLong"
                                :label="$t('name-long')"
                                :error-messages="errors.nameLong"
                                :required="true"
                                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('nameLong', true)"
                            />
                            <NumberField
                                :value="values.year"
                                :label="$t('year')"
                                :error-messages="errors.year"
                                :required="true"
                                @input="setFieldValue('year', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('year', true)"
                            />
                            <TextAreaField
                                :value="values.description ?? ''"
                                :label="$t('description')"
                                :error-messages="errors.description"
                                :max-length="descriptionMaxLength"
                                :required="false"
                                @input="setFieldValue('description', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('description', true)"
                            />
                        </template>
                    </BaseForm>
                    <div v-else>
                        <CardRowText
                            :label="$t('name-short')"
                            :text="valueToString(event.nameShort)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('name-long')"
                            :text="valueToString(event.nameLong)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('year')"
                            :text="valueToString(event.year?.toString())"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('description')"
                            :text="valueToString(event.description)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
        <FlexibleCard :headline-id="organisationCardId" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus(organisationCardId)"
                    :edit-button-disabled-explanation="editButtonDisabledMessage(organisationCardId)"
                    :save-button-disabled-explanation="saveButtonDisabledMessage(organisationCardId)"
                    @enter-edit-mode="onEnterEditMode(organisationCardId, organisationCardLabel)"
                    @leave-edit-mode="onLeaveEditMode"
                    @discard-changes="onDiscardChanges"
                    @save="onSave"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ organisationCardLabel }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>
            <template #body>
                <ContentFormTransition>
                    <BaseForm v-if="showForm(organisationCardId)" class="p-5" @submit.prevent>
                        <template #fields>
                            <TextAreaField
                                :value="values.contact ?? ''"
                                :label="$t('contact')"
                                :error-messages="errors.contact"
                                :required="false"
                                @input="setFieldValue('contact', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('contact', true)"
                            />
                        </template>
                    </BaseForm>
                    <div v-else>
                        <CardRowText
                            :label="$t('contact')"
                            :text="valueToString(event.contact)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
        <FlexibleCard :headline-id="linksCardId" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus(linksCardId)"
                    :edit-button-disabled-explanation="editButtonDisabledMessage(linksCardId)"
                    :save-button-disabled-explanation="saveButtonDisabledMessage(linksCardId)"
                    @enter-edit-mode="onEnterEditMode(linksCardId, linksCardLabel)"
                    @leave-edit-mode="onLeaveEditMode"
                    @discard-changes="onDiscardChanges"
                    @save="onSave"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ linksCardLabel }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>
            <template #body>
                <ContentFormTransition>
                    <BaseForm v-if="showForm(linksCardId)" class="p-5" @submit.prevent>
                        <template #fields>
                            <MultiLink
                                :label="$t('external-event-links-field-label')"
                                :links="values.links"
                                :error-messages="errors.links"
                                @input="setFieldValue('links', $event)"
                                @delete="setFieldValue('links', $event)"
                            />
                        </template>
                    </BaseForm>
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="links.length > 0"
                            :label="$t('external-event-links-field-label')"
                            :items="links"
                            :has-border-bottom="false"
                        />
                        <CardRowText
                            v-else
                            :label="$t('external-event-links-field-label')"
                            text="-"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>
<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowAttachmentGroup from '@/components/Card/CardRowAttachmentGroup.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import NumberField from '@/components/Form/NumberField.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import { toCardRowAttachmentLink } from '@/components/MultiLink/multilink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useExternalEventInjection } from '@/external_event/composables/useExternalEventInjection';
import { getExternalEventEventUpdateSchema } from '@/external_event/schemas';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { useForm } from '@/forms/composables/useForm';
import { valueToString } from '@/forms/forms';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { computed } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const fluent = useFluent();

const { event, onUpdateEvent, updateLocation, createExternalEventMultiLink } = useExternalEventInjection();

const {
    activeCard,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    onEnterEditMode,
    showForm,
    getCardStatus,
    resetActiveCard,
} = useActiveCard();

const identifiersCardId = 'external-event-event-masterdata';
const identifiersCardLabel = fluent.$t('identifiers');

const organisationCardId = 'external-event-event-organisation';
const organisationCardLabel = fluent.$t('external-event-organisation');

const linksCardId = 'external-event-event-links';
const linksCardLabel = fluent.$t('external-event-links-card-label');

const ExternalEventEventUpdateSchema = getExternalEventEventUpdateSchema(event.value?.id ?? '');
const descriptionMaxLength = ExternalEventEventUpdateSchema.shape.description.maxLength ?? undefined;

const linksId = computed(() => event.value?.links.id);
const linksLinks = computed(() => event.value?.links?.links.nodes ?? []);
const links = computed(() => toCardRowAttachmentLink(linksLinks.value));

const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createExternalEventMultiLink);

const { values, errors, setFieldTouched, setFieldValue, validate, discardForm, submit } = useForm(
    ExternalEventEventUpdateSchema,
    computed(() => ({
        nameShort: event.value?.nameShort ?? '',
        nameLong: event.value?.nameLong ?? '',
        year: event.value?.year ?? null,
        description: event.value?.description ?? '',
        contact: event.value?.contact ?? '',
        links: getFormFieldLinks(linksLinks),
    })),
);

function onLeaveEditMode() {
    discardForm();
    resetActiveCard();
}

function onDiscardChanges() {
    discardForm();
    resetActiveCard();
}

async function onSave() {
    await submit(async () => {
        const valid = await validate();
        if (valid && event.value) {
            const nameShortChanged = values.value.nameShort !== event.value.nameShort;
            const updated = await onUpdateEvent(values.value);
            if (updated) {
                // if nameShort changed, update also linked location name
                if (event.value.locationId && nameShortChanged) {
                    const updatedLocation = await updateLocation(event.value.locationId, values.value.nameShort);
                    if (!updatedLocation) {
                        throw Error('Error on updating location name of event');
                    }
                }
            }
            await saveMultiLinkFields([{ id: linksId.value, fieldName: 'links', links: values.value.links }]);
            discardForm();
            resetActiveCard();
        }
    });
}

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        discardForm();
    }
    resetActiveCard();
    return true;
});

useHeadSafe({
    title: () =>
        `${fluent.$t('master-data')} - ${event.value?.nameShort} - ${event.value?.series?.nameShort} - ${fluent.$t(
            'external-events',
        )}`,
});
</script>
